import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchLondonContent,
  fetchLondonState,
  postLondonAction,
  type ActionResult,
  type LondonAction,
  type LondonState,
} from '../api/londonApi';

export const londonKeys = {
  content: ['london', 'content'] as const,
  state: ['london', 'state'] as const,
};

/** Содержание главы статично: берём один раз за сеанс. */
export function useLondonContent() {
  return useQuery({ queryKey: londonKeys.content, queryFn: fetchLondonContent, staleTime: Infinity, retry: 1 });
}

export function useLondonState() {
  return useQuery({ queryKey: londonKeys.state, queryFn: fetchLondonState, staleTime: Infinity, refetchOnMount: 'always', retry: 1 });
}

/** Очередь действий: следующее уходит после ответа на предыдущее и берёт свежую версию сохранения. Без неё два
 *  действия подряд (открыть чат → «прочитано» + «следить за целью») уходили с одной версией, второе получало 409,
 *  и игрок видел «Игра открыта в другом окне» при одном окне (проход 2026-11-03). */
let actionChain: Promise<unknown> = Promise.resolve();

/** Действие игрока: сервер проверяет и возвращает новое состояние. При конфликте версий — перечитываем состояние. */
export function useLondonAction() {
  const qc = useQueryClient();
  return useMutation<ActionResult, unknown, LondonAction>({
    mutationFn: (action) => {
      const run = () =>
        postLondonAction(action, qc.getQueryData<LondonState>(londonKeys.state)?.version ?? 0).then((res) => {
          // Сразу в кэш: следующее действие в очереди должно взять уже новую версию.
          qc.setQueryData(londonKeys.state, res.state);
          return res;
        });
      const next = actionChain.then(run, run);
      actionChain = next.catch(() => undefined);
      return next;
    },
    onSuccess: (res) => {
      qc.setQueryData(londonKeys.state, res.state);
    },
    onError: (err) => {
      const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
      if (code === 'LONDON_VERSION_CONFLICT') void qc.invalidateQueries({ queryKey: londonKeys.state });
    },
  });
}

/** Код ошибки сервера из ответа axios (формат `error.code`, как у других модулей). */
export function londonErrorCode(err: unknown): string | null {
  const data = (err as { response?: { data?: { error?: { code?: string }; detail?: { code?: string } } } })?.response?.data;
  return data?.error?.code ?? data?.detail?.code ?? null;
}
